/**
 * Authentication API client for JWT token management.
 * 
 * Flow:
 * 1. Request challenge from backend
 * 2. Sign challenge with wallet
 * 3. Verify signature and receive JWT token
 */

import { apiClient } from "./api-client";

const TOKEN_STORAGE_KEY = "nordex_auth_token";
const TOKEN_EXPIRY_KEY = "nordex_auth_token_expiry";
const TOKEN_WALLET_KEY = "nordex_auth_wallet";
const TOKEN_USER_ID_KEY = "nordex_auth_user_id";
const TOKEN_EMAIL_KEY = "nordex_auth_email";
const TOKEN_LOGIN_METHOD_KEY = "nordex_auth_login_method";

/** Challenge response from backend */
export interface ChallengeResponse {
  nonce: string;
  message: string;
  expires_at: number;
}

/** Verify request body */
export interface VerifyRequest {
  wallet: string;
  signature: string;
  nonce: string;
}

/** Verify response with JWT token */
export interface VerifyResponse {
  token: string;
  expires_at: number;
  user_id: string;
  /** User's linked email (if any) - for wallet users who linked email */
  email?: string;
  /** Login method: 'wallet', 'privy', or 'both' (wallet user who linked email) */
  login_method?: string;
  /** Whether this is a brand new user (first signup) - used for Meta Pixel Lead tracking */
  is_new_user?: boolean;
}

/**
 * Request an authentication challenge for a wallet address.
 */
export async function getAuthChallenge(wallet: string): Promise<ChallengeResponse> {
  return apiClient.get<ChallengeResponse>(`/api/auth/challenge?wallet=${encodeURIComponent(wallet)}`);
}

/**
 * Verify a signed challenge and get JWT token.
 */
export async function verifyAuth(request: VerifyRequest): Promise<VerifyResponse> {
  return apiClient.post<VerifyResponse>("/api/auth/verify", request);
}

/**
 * Store JWT token in localStorage along with wallet address, user ID, email, and login method.
 */
export function storeToken(token: string, expiresAt: number, wallet: string, userId?: string, email?: string, loginMethod?: string): void {
  try {
    localStorage.setItem(TOKEN_STORAGE_KEY, token);
    localStorage.setItem(TOKEN_EXPIRY_KEY, expiresAt.toString());
    localStorage.setItem(TOKEN_WALLET_KEY, wallet);
    if (userId) {
      localStorage.setItem(TOKEN_USER_ID_KEY, userId);
    }
    if (email) {
      localStorage.setItem(TOKEN_EMAIL_KEY, email);
    } else {
      localStorage.removeItem(TOKEN_EMAIL_KEY);
    }
    if (loginMethod) {
      localStorage.setItem(TOKEN_LOGIN_METHOD_KEY, loginMethod);
    } else {
      localStorage.removeItem(TOKEN_LOGIN_METHOD_KEY);
    }
  } catch {
    // localStorage may not be available
    console.warn("Failed to store auth token");
  }
}

/**
 * Get stored JWT token if valid.
 * Optionally validates that the token belongs to the specified wallet.
 */
export function getStoredToken(forWallet?: string): string | null {
  try {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const expiryStr = localStorage.getItem(TOKEN_EXPIRY_KEY);
    const storedWallet = localStorage.getItem(TOKEN_WALLET_KEY);
    
    if (!token || !expiryStr) {
      return null;
    }
    
    // If wallet specified, verify token belongs to this wallet
    if (forWallet && storedWallet && storedWallet !== forWallet) {
      // Token belongs to a different wallet - clear it
      clearStoredToken();
      return null;
    }
    
    const expiry = parseInt(expiryStr, 10);
    const now = Math.floor(Date.now() / 1000);
    
    // Check if token is expired (with 5 minute buffer)
    if (expiry < now + 300) {
      clearStoredToken();
      return null;
    }
    
    return token;
  } catch (err) {
    console.error(`[getStoredToken] Error:`, err);
    return null;
  }
}

/**
 * Get the wallet address associated with the stored token.
 */
export function getStoredWallet(): string | null {
  try {
    return localStorage.getItem(TOKEN_WALLET_KEY);
  } catch {
    return null;
  }
}

/**
 * Get the user ID (database UUID) associated with the stored token.
 */
export function getStoredUserId(): string | null {
  try {
    return localStorage.getItem(TOKEN_USER_ID_KEY);
  } catch {
    return null;
  }
}

/**
 * Get the user email associated with the stored token.
 * Only set for wallet users who have linked an email.
 */
export function getStoredEmail(): string | null {
  try {
    return localStorage.getItem(TOKEN_EMAIL_KEY);
  } catch {
    return null;
  }
}

/**
 * Get the login method associated with the stored token.
 * Values: 'wallet', 'privy', or 'both' (wallet user who linked email)
 */
export function getStoredLoginMethod(): string | null {
  try {
    return localStorage.getItem(TOKEN_LOGIN_METHOD_KEY);
  } catch {
    return null;
  }
}

/**
 * Clear stored JWT token and associated data.
 */
export function clearStoredToken(): void {
  try {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_KEY);
    localStorage.removeItem(TOKEN_WALLET_KEY);
    localStorage.removeItem(TOKEN_USER_ID_KEY);
    localStorage.removeItem(TOKEN_EMAIL_KEY);
    localStorage.removeItem(TOKEN_LOGIN_METHOD_KEY);
  } catch {
    // Ignore
  }
}

/**
 * Check if token needs refresh (expires in less than 1 hour).
 */
export function tokenNeedsRefresh(): boolean {
  try {
    const expiryStr = localStorage.getItem(TOKEN_EXPIRY_KEY);
    if (!expiryStr) return true;
    
    const expiry = parseInt(expiryStr, 10);
    const now = Math.floor(Date.now() / 1000);
    
    // Refresh if expiring in less than 1 hour
    return expiry < now + 3600;
  } catch {
    return true;
  }
}

/**
 * Get token expiry timestamp.
 */
export function getTokenExpiry(): number | null {
  try {
    const expiryStr = localStorage.getItem(TOKEN_EXPIRY_KEY);
    if (!expiryStr) return null;
    return parseInt(expiryStr, 10);
  } catch {
    return null;
  }
}
